import { useState } from 'react'
import { AddressesTabView, UserRole, ApproveMfaDialog } from '@valiant/ui-shared'
import { ManageWhitelistedAddressModal } from '../modals/ManageWhitelistedAddressModal'
import { AddWhitelistedAddressModal } from '../modals/AddWhitelistedAddressModal'
import {
  useGetCompanyWhitelistedAddressesQuery,
  useApproveWhitelistedAddressMutation,
  useRejectWhitelistedAddressMutation,
  WhitelistedAddressStatus,
  type WhitelistedAddress,
} from '../../generated/graphql'
import { toast } from 'sonner'
import { useAuth } from '../../AuthProvider'
import { environment } from '../../environment'

interface AddressesTabProps {
  companyId?: string
  searchTerm?: string
}

export function AddressesTab({ companyId, searchTerm }: AddressesTabProps) {
  const [isManageModalOpen, setIsManageModalOpen] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<WhitelistedAddress | null>(null)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isMfaDialogOpen, setIsMfaDialogOpen] = useState(false)
  const [pendingApproveId, setPendingApproveId] = useState<string | null>(null)

  const { auth } = useAuth()
  const isGroupOwner = auth?.role === UserRole.GroupOwner

  const [{ data: addressesData, fetching: addressesLoading, error: addressesError }] =
    useGetCompanyWhitelistedAddressesQuery({
      variables: { companyId: companyId! },
      pause: !companyId,
    })

  const [{ fetching: approveLoading }, approveAddress] = useApproveWhitelistedAddressMutation()
  const [, rejectAddress] = useRejectWhitelistedAddressMutation()

  const addresses = addressesData?.getCompanyWhitelistedAddresses || []

  const handleApprove = (id: string) => {
    setPendingApproveId(id)
    setIsMfaDialogOpen(true)
  }

  const handleMfaConfirm = async (proof: { mfaCode?: string; passkeyApproval?: string }) => {
    if (!pendingApproveId) return
    try {
      const result = await approveAddress({ input: { id: pendingApproveId, ...proof } })
      if (result.error) {
        const errorMessage = result.error.message
        if (errorMessage.includes('Invalid MFA code')) {
          toast.error('Invalid MFA code')
        } else {
          toast.error('Something went wrong. Please try again.')
        }
        return
      }
      if (result.data) {
        toast.success('Whitelisted Address has successfully been approved!')
        setIsMfaDialogOpen(false)
        setPendingApproveId(null)
      } else {
        toast.error('Something went wrong. Please try again.')
      }
    } catch (error) {
      console.error('Error approving address:', error)
      toast.error('Something went wrong. Please try again.')
    }
  }

  const handleReject = async (id: string) => {
    try {
      const result = await rejectAddress({ input: { id } })
      if (result.data) {
        toast.success('Whitelisted Address has successfully been rejected!')
      } else {
        toast.error('Something went wrong. Please try again.')
      }
    } catch (error) {
      console.error('Error rejecting address:', error)
      toast.error('Something went wrong. Please try again.')
    }
  }

  const handleManage = (address: WhitelistedAddress) => {
    setSelectedAddress(address)
    setIsManageModalOpen(true)
  }

  return (
    <>
      <AddressesTabView
        companyId={companyId}
        searchTerm={searchTerm}
        addresses={addresses}
        loading={addressesLoading}
        error={addressesError}
        onApprove={isGroupOwner ? handleApprove : undefined}
        onReject={isGroupOwner ? handleReject : undefined}
        onManage={handleManage}
        onAddClick={() => setIsAddModalOpen(true)}
        showApproveButton={(status) =>
          isGroupOwner && status === WhitelistedAddressStatus.Requested
        }
        showRejectButton={(status) => isGroupOwner && status === WhitelistedAddressStatus.Requested}
        showManageButton={(status) => status === WhitelistedAddressStatus.Approved}
        gridLayout="compact"
      />

      <ApproveMfaDialog
        open={isMfaDialogOpen}
        title="Approve Address"
        onOpenChange={setIsMfaDialogOpen}
        onConfirm={handleMfaConfirm}
        isSubmitting={approveLoading}
        apiUrl={environment.API_URL}
        token={auth?.token}
        action="approveWhitelistedAddress"
        targetKey={pendingApproveId ?? undefined}
      />

      <ManageWhitelistedAddressModal
        open={isManageModalOpen}
        onOpenChange={setIsManageModalOpen}
        address={selectedAddress}
      />

      <AddWhitelistedAddressModal
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        companyId={companyId!}
      />
    </>
  )
}
